<template>
    <stat-card :expandable="expandable" :expanded="expanded" @toggle:expand="$emit('toggle:expand')">
        <template #header>
            <kimsa-combobox class="acwr-metric" dense solo :items="typeOptions" @change="type = $event" :value="type"
                placeholder-class no-object height="30" />
        </template>

        <div v-if="hasCompare" class="acwr-compare">
            <div class="acwr-block">
                <div class="acwr-block-lbl">{{ mainTitle }}</div>
                <div class="hero-stats">
                    <div class="hero-stat">
                        <div class="hero-label">{{ $t('statistics.acute') }}</div>
                        <div class="hero-number sm">{{ mainAcute }}</div>
                    </div>
                    <div class="hero-divider" />
                    <div class="hero-stat">
                        <div class="hero-label chronic">{{ $t('statistics.chronic') }}</div>
                        <div class="hero-number sm chronic">{{ mainChronic }}</div>
                    </div>
                </div>
                <div class="ratio-bar">{{ $t('statistics.ratio') }}
                    <span class="ratio-val" :class="ratioClass(mainRatio)">{{ mainRatio }}</span>
                </div>
            </div>
            <div class="acwr-block acwr-block--team">
                <div class="acwr-block-lbl team">{{ compareTitle }}</div>
                <div class="hero-stats">
                    <div class="hero-stat">
                        <div class="hero-label">{{ $t('statistics.acute') }}</div>
                        <div class="hero-number sm">{{ compareAcute }}</div>
                    </div>
                    <div class="hero-divider" />
                    <div class="hero-stat">
                        <div class="hero-label chronic">{{ $t('statistics.chronic') }}</div>
                        <div class="hero-number sm chronic">{{ compareChronic }}</div>
                    </div>
                </div>
                <div class="ratio-bar">{{ $t('statistics.ratio') }}
                    <span class="ratio-val" :class="ratioClass(compareRatio)">{{ compareRatio }}</span>
                </div>
            </div>
        </div>

        <div v-else class="acwr-single">
            <div class="hero-stats">
                <div class="hero-stat">
                    <div class="hero-label">{{ $t('statistics.acute') }}</div>
                    <div class="hero-number">{{ mainAcute }}</div>
                </div>
                <div class="hero-divider" />
                <div class="hero-stat">
                    <div class="hero-label chronic">{{ $t('statistics.chronic') }}</div>
                    <div class="hero-number chronic">{{ mainChronic }}</div>
                </div>
            </div>
            <div class="ratio-bar">{{ $t('statistics.ratio') }}
                <span class="ratio-val" :class="ratioClass(mainRatio)">{{ mainRatio }}</span>
            </div>
        </div>
    </stat-card>
</template>

<script>
import { calculateWellnessPreInfo } from '../../utils/Routine';
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';
import StatCard from './StatCard.vue';
import { expandableCardMixin } from '@/mixins/expandableCard';

export default {
    name: "AcuteChronicChart",
    components: { KimsaCombobox, StatCard },
    mixins: [expandableCardMixin],
    props: {
        docs: {
            type: Array,
            default: null,
        },
        compareDocs: {
            type: Array,
            default: null,
        },
        mainLabel: {
            type: String,
            default: null,
        },
        compareLabel: {
            type: String,
            default: null,
        }
    },
    data(vm) {
        return {
            typeOptions: [
                { name: vm.$t('statistics.wellness'), value: 'wellness' },
                { name: vm.$t('statistics.charge'), value: 'charge' },
                { name: vm.$t('statistics.sets_total'), value: 'sets_total' },
                { name: vm.$t('statistics.sets_strength'), value: 'sets_strength' },
                { name: vm.$t('statistics.sets_speed'), value: 'sets_speed' },
                { name: vm.$t('statistics.sets_resistance'), value: 'sets_resistance' },
                { name: vm.$t('statistics.intensity_strength'), value: 'intensity_strength' },
                { name: vm.$t('statistics.tonnage'), value: 'tonnage' },
                { name: vm.$t('statistics.meters'), value: 'distance' },
                { name: vm.$t('statistics.minutes'), value: 'minutes' },
            ],
            type: 'wellness',
            rpeTable: [
                { name: 10, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 9.5, reps: [97, 93, 89, 86, 84, 82, 80, 77.5] },
                { name: 9, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 8.5, reps: [93, 89, 86, 84, 82, 80, 77.5, 74.5] },
                { name: 8, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 7.5, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 7, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            rirTable: [
                { name: 0, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 1, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 2, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 3, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 4, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            acute: {},
            chronic: {},
            acuteCompare: {},
            chronicCompare: {},
        }
    },
    computed: {
        hasCompare(vm = this) {
            return !!(vm.compareDocs && vm.compareDocs.length)
        },
        mainTitle(vm = this) {
            return vm.mainLabel || vm.$t('common.student')
        },
        compareTitle(vm = this) {
            return vm.compareLabel || vm.$t('common.team')
        },
        mainAcute(vm = this) {
            return vm.acute[vm.type] || 0
        },
        mainChronic(vm = this) {
            return vm.weekAverage(vm.chronic[vm.type])
        },
        mainRatio(vm = this) {
            return vm.ratioOf(vm.mainAcute, vm.mainChronic)
        },
        compareAcute(vm = this) {
            return vm.acuteCompare[vm.type] || 0
        },
        compareChronic(vm = this) {
            return vm.weekAverage(vm.chronicCompare[vm.type])
        },
        compareRatio(vm = this) {
            return vm.ratioOf(vm.compareAcute, vm.compareChronic)
        }
    },
    mounted() {
        const vm = this
        vm.setAcuteChronic()
    },
    methods: {
        weekAverage(value) {
            const number = Number(value)
            return !isNaN(number) ? (number / 4).toFixed(1).replace('.0', '') : 0
        },
        ratioOf(acute, chronic) {
            const ratio = Number(chronic) > 0
                ? (Number(acute) / Number(chronic)).toFixed(1).replace('.0', '')
                : 0
            return ratio === 'NaN' ? 0 : ratio
        },
        ratioClass(ratio) {
            const n = Number(ratio)
            if (!Number.isFinite(n)) return 'low'
            if (n >= 1.5) return 'high'
            if (n >= 1.3) return 'warn'
            if (n >= 0.8) return 'ok'
            return 'low'
        },
        setAcuteChronic() {
            const vm = this;
            const main = vm.loadAcuteChronic(vm.docs || []);
            vm.acute = main.acute;
            vm.chronic = main.chronic;

            const compare = vm.loadAcuteChronic(vm.compareDocs || []);
            vm.acuteCompare = compare.acute;
            vm.chronicCompare = compare.chronic;
        },
        loadAcuteChronic(docs = null) {
            if (!docs) return;
            const vm = this;

            const endDate = vm.$moment().endOf('week');
            const startDate = vm.$moment(endDate).subtract(4, 'weeks').startOf('week');
            let currentDate = startDate.clone();
            const weekData = { acute: [], chronic1: [], chronic2: [], chronic3: [], chronic4: [] };
            let dayCount = 0;

            // Separar las rutinas por semanas
            while (currentDate.isSameOrBefore(endDate)) {
                const currentRoutine = docs.find(routine => routine.rawDate.full === currentDate.format('YYYY-MM-DD')) || null;
                currentDate.add(1, 'day');
                dayCount++;

                if (currentRoutine) {
                    const week = this.getWeekByDayCount(dayCount);
                    weekData[week].push(currentRoutine);
                }
            }

            let acute = this.sumStats(this.loadStats(weekData.acute));
            const chronicTotals = ['chronic1', 'chronic2', 'chronic3', 'chronic4'].map(week => this.sumStats(this.loadStats(weekData[week])));

            let chronic = this.sumChronicWeeks(chronicTotals);

            return { acute, chronic }
        },
        getWeekByDayCount(dayCount) {
            if (dayCount > 28) return 'acute';
            if (dayCount > 21) return 'chronic4';
            if (dayCount > 14) return 'chronic3';
            if (dayCount > 7) return 'chronic2';
            return 'chronic1';
        },
        sumChronicWeeks(chronicWeeks) {
            return chronicWeeks.reduce((totals, weekStats) => {
                Object.keys(weekStats).forEach(key => {
                    totals[key] = (totals[key] || 0) + (Number(weekStats[key]) || 0);
                });
                return totals;
            }, {});
        },
        sumStats(routines) {
            let sumRoutines = routines.reduce((totals, stats) => {
                Object.keys(stats).forEach(key => {
                    const value = stats[key] === 'NaN' ? 0 : Number(stats[key])
                    if (key !== 'date') {
                        totals[key] = (totals[key] || 0) + (value || 0)
                        if (value > 0 && (key === 'wellness' || key === 'intensity_strength')) {
                            totals[`${key}_count`] = (totals[`${key}_count`] || 0) + 1;
                        }
                    }
                });
                return totals;
            }, {});
            return {
                ...sumRoutines,
                wellness: Number(sumRoutines.wellness / (sumRoutines.wellness_count || 1)).toFixed(0),
                intensity_strength: Number(sumRoutines.intensity_strength / (sumRoutines.intensity_strength_count || 1)).toFixed(0),
            }
        },
        loadStats(routines) {
            const vm = this;
            if (routines.length === 0) routines.push({})
            return routines.map(doc => {
                const preInfo = doc?.advance?.preInfo || null
                const wellness = calculateWellnessPreInfo(preInfo)
                const postInfo = doc?.advance?.postInfo || null
                const duration = Number(postInfo?.duration || 0)
                const effort = Number(postInfo?.effort || 0)
                const charge = duration * effort
                const minutes = doc.type === 'competence' ? duration : 0

                let sets_total = 0
                let sets_strength = 0
                let sets_speed = 0
                let sets_resistance = 0
                let percentage = 0
                let rpe = 0
                let rir = 0
                let tonnage = 0
                let distance = 0
                let quantity_strong = 0
                doc.blocks?.forEach(block => {
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest') {
                            let sets = Number(exercise.config?.sets?.length || 0)
                            sets_total += sets
                            if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.STRONG) sets_strength += sets
                            if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.SPEED) sets_speed += sets
                            if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.RESISTANCE) sets_resistance += sets

                            exercise.config?.sets?.forEach((set, i) => {
                                if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.STRONG) {
                                    let reps = vm.takeValueFromSet(set, 'reps') || 0
                                    tonnage += (vm.takeValueFromSet(set, 'weight') * reps)
                                    let valuePercentage = vm.takeValueFromSet(set, 'percentage')
                                    percentage += valuePercentage
                                    let valueRpe = vm.takeValueFromSet(set, 'rpe')
                                    rpe += valueRpe ? vm.getValueFromTable(valueRpe, set, 'rpeTable') : 0
                                    let valueRir = vm.takeValueFromSet(set, 'rir')
                                    rir += valueRir ? vm.getValueFromTable(valueRir, set, 'rirTable') : 0
                                    if (valuePercentage > 0 || valueRir > 0 || valueRpe > 0) quantity_strong++
                                } else if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.SPEED || vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.RESISTANCE) {
                                    distance += (vm.takeValueFromSet(set, 'distance') * sets)
                                }
                            })
                        }
                    })
                })

                let intensity_strength = percentage + rpe + rir
                if (quantity_strong > 0) intensity_strength /= quantity_strong

                return {
                    date: doc.rawDate?.full || null,
                    wellness: wellness,
                    charge: charge,
                    sets_total: sets_total,
                    sets_strength: sets_strength,
                    sets_speed: sets_speed,
                    sets_resistance: sets_resistance,
                    intensity_strength: Number(intensity_strength).toFixed(1).replace('.0', ''),
                    tonnage: tonnage,
                    distance: distance,
                    minutes: minutes,
                }
            })
        },
        takeValueFromSet(set, attribute) {
            const vm = this
            let defaultValue = 0
            let value = set.data.find(data => data.short_name === `short_${attribute}`)
            return !!value || value === 0 ? Number(value.value || 0) : defaultValue
        },
        getValueFromTable(value, set, typeTable) {
            const vm = this
            if (typeTable === 'rpeTable') {
                if (value > 10) value = 10
                if (value < 7) value = 7
            }
            if (typeTable === 'rirTable') {
                if (value > 4) value = 4
                if (value < 0) value = 0
            }
            let findValueTable = vm[typeTable]?.find(table => table.name == value) || null
            let reps = set.data.find(data => data.short_name === 'short_reps') || null
            let repsValue = reps && (Number(reps.value || 0) - 1) || 0
            if (repsValue > 7) repsValue = 7
            if (repsValue < 0) repsValue = 0
            return findValueTable && repsValue && findValueTable.reps[repsValue] || 0
        },
    },
    watch: {
        docs() {
            this.setAcuteChronic()
        },
        compareDocs() {
            this.setAcuteChronic()
        },
    }
}
</script>

<style scoped>
.acwr-metric {
    flex: 1;
    max-width: 200px;
}

.acwr-metric ::v-deep .v-input,
.acwr-metric ::v-deep .v-text-field {
    margin: 0;
    padding: 0;
}

.acwr-metric ::v-deep .v-input__control,
.acwr-metric ::v-deep .v-input__slot {
    min-height: 30px !important;
}

.acwr-metric ::v-deep .v-input__slot {
    padding: 0 10px !important;
}

.acwr-metric ::v-deep .v-select__selection {
    font-size: 12px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: normal;
    color: var(--v-txtSecondary-base);
}

.acwr-metric ::v-deep .v-input__append-inner {
    margin-top: 0;
    align-self: center;
}

.acwr-single {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 18px;
}

.hero-stats {
    display: flex;
    align-items: center;
    justify-content: space-around;
}

.hero-divider {
    width: 1px;
    align-self: stretch;
    background: var(--v-outline-base);
}

.hero-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    flex: 1;
}

.hero-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--v-txtMuted-base);
}

.hero-label.chronic {
    color: var(--v-secondary-base);
}

.hero-number {
    font-size: 44px;
    font-weight: 800;
    line-height: 1;
    color: var(--v-txtPrimary-base);
}

.hero-number.chronic {
    color: var(--v-secondary-base);
}

.hero-number.sm {
    font-size: 26px;
}

.ratio-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--v-txtMuted-base);
}

.ratio-val {
    font-size: 15px;
    font-weight: 800;
}

.ratio-val.ok {
    color: var(--v-success-base);
}

.ratio-val.warn {
    color: #F5C842;
}

.ratio-val.high {
    color: var(--v-error-base);
}

.ratio-val.low {
    color: var(--v-txtMuted-base);
}

.acwr-compare {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    justify-content: center;
}

.acwr-block {
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.acwr-block--team {
    border-color: rgba(102, 204, 204, 0.25);
    background: rgba(102, 204, 204, 0.04);
}

.acwr-block-lbl {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--v-txtMuted-base);
}

.acwr-block-lbl.team {
    color: var(--v-secondary-base);
}

.acwr-block .hero-label {
    font-size: 8px;
}

.acwr-block .ratio-bar {
    font-size: 9px;
    justify-content: flex-start;
}

.acwr-block .ratio-val {
    font-size: 12px;
}
</style>